import { type SolarElectricalChange } from './solarElectricalChange';
import {
  SOLAR_ISC_IRRADIANCE_FACTOR,
  solarColdVocFactor,
} from './solarStringCheck';

/**
 * The designer's inverter-unit rules as plain functions, so the GraphQL API
 * adds units, declares inputs and caps string length exactly as the
 * Electrical tab does. ElectricalAllocationPanel.tsx imports these.
 */

/** Longest string Auto-string makes, whatever the voltage allows. */
export const SOLAR_MAX_PANELS_PER_STRING = 13;

/**
 * Input voltage assumed for a unit whose catalogue row has no
 * maxInputVoltageV: the NEC 690.7(C) one- and two-family dwelling limit and
 * the lowest common residential string-inverter rating.
 */
export const SOLAR_ASSUMED_MAX_INPUT_VOLTAGE_V = 600;

export type SolarInverterUnitSpec = {
  name: string;
  /** Catalogue manufacturer, when known; "SolarEdge" marks an optimiser unit. */
  manufacturer?: string | null;
  mpptCount?: number | null;
  hybrid?: boolean | null;
  pvConversion?: { mpptCount?: number | null } | null;
  /** Physical DC inputs per MPPT from the catalogue; unknown means one. */
  inputsPerMppt?: number | null;
  /** Short-circuit current limit per MPPT (A). */
  maxMpptIscA?: number | null;
};

export const solarInverterMpptCount = (
  spec: SolarInverterUnitSpec | null | undefined
): number | null => spec?.mpptCount ?? spec?.pvConversion?.mpptCount ?? null;

/**
 * SolarEdge-style optimiser inverters: fixed-voltage DC, no string window.
 * Their datasheets print no MPPT count because tracking is in the optimisers.
 */
export const isSolarOptimiserInverter = (
  spec: SolarInverterUnitSpec | null | undefined
): boolean =>
  !!spec &&
  /solaredge|optimi[sz]/i.test(`${spec.manufacturer ?? ''} ${spec.name}`) &&
  !spec.hybrid &&
  spec.mpptCount == null;

/** Badge reason for a string on an optimiser unit: no 1.25 × Isc or Voc window applies. */
export const SOLAR_OPTIMISER_STRING_REASON =
  'Optimiser system: the optimisers fix the string voltage, so no inverter voltage or per-input current check applies';

/** Tracker key a unit gets when its catalogue row has no MPPT count. */
export const SOLAR_ASSUMED_TRACKER_KEY = 'Input 1';

/**
 * What the designer calls a unit's tracker. An optimiser unit's trackers are
 * its optimiser string inputs, whatever their saved key. A tracker this file
 * assumed ("Input n", on a product with no MPPT count) reads
 * "MPPT n (assumed)". Every other tracker shows its own key.
 */
export const solarTrackerLabel = (
  spec: SolarInverterUnitSpec | null | undefined,
  trackerKey: string,
  trackerIndex: number,
  trackerCount = 1
): string => {
  if (!spec || solarInverterMpptCount(spec) != null) return trackerKey;
  if (isSolarOptimiserInverter(spec))
    return trackerCount > 1
      ? `Optimiser string input ${trackerIndex + 1}`
      : 'Optimiser string input';
  return /^Input \d+$/.test(trackerKey)
    ? `MPPT ${trackerIndex + 1} (assumed)`
    : trackerKey;
};

/** Catalogue inputs per MPPT; one when the catalogue does not say. */
export const solarInputsPerMppt = (
  spec: SolarInverterUnitSpec | null | undefined
): number => {
  const declared = spec?.inputsPerMppt;
  return declared != null && Number.isInteger(declared) && declared >= 1
    ? declared
    : 1;
};

/**
 * Strings Auto-string may parallel on one MPPT: one unless the catalogue
 * declares several inputs per MPPT and a per-MPPT short-circuit limit that
 * 1.25 × Isc × strings fits. Optimiser units take two on their input stage.
 */
export const solarMaxStringsPerMppt = (args: {
  spec: SolarInverterUnitSpec | null | undefined;
  /** Highest panel Isc on the design (A). */
  panelIscA?: number | null;
}): number => {
  if (isSolarOptimiserInverter(args.spec)) return 2;
  const inputs = solarInputsPerMppt(args.spec);
  const limit = args.spec?.maxMpptIscA;
  const isc = args.panelIscA;
  if (inputs < 2 || limit == null || !(limit > 0) || isc == null || !(isc > 0))
    return 1;
  return Math.max(
    1,
    Math.min(inputs, Math.floor(limit / (isc * SOLAR_ISC_IRRADIANCE_FACTOR)))
  );
};

/**
 * The MPPTs and DC inputs a new unit gets from its catalogue: MPPT count ×
 * inputs per MPPT (one when the catalogue does not say); input keys number on
 * across MPPTs. Without an MPPT count the unit gets one assumed tracker,
 * "Input 1": an optimiser unit takes two strings on that input stage, any
 * other unit one DC input. Never empty for a known product, so nobody has to
 * declare an input by hand.
 */
export const solarTrackerDeclarations = (
  inverterInstanceId: string,
  spec: SolarInverterUnitSpec | null | undefined
): SolarElectricalChange[] => {
  const assumed = (inputKeys: string[]) =>
    inputKeys.map((inputKey) => ({
      type: 'declareInput' as const,
      inverterInstanceId,
      mpptKey: SOLAR_ASSUMED_TRACKER_KEY,
      inputKey,
    }));
  if (!spec) return [];
  if (isSolarOptimiserInverter(spec)) return assumed(['DC 1', 'DC 2']);
  if (solarInverterMpptCount(spec) == null) return assumed(['DC 1']);
  const mppts = solarInverterMpptCount(spec) ?? 0;
  const perMppt = solarInputsPerMppt(spec);
  return Array.from({ length: mppts }).flatMap((_, mppt) =>
    Array.from({ length: perMppt }, (__, input) => ({
      type: 'declareInput' as const,
      inverterInstanceId,
      mpptKey: `MPPT ${mppt + 1}`,
      inputKey: `DC ${mppt * perMppt + input + 1}`,
    }))
  );
};

/** "Inverter A", "Inverter B"… skipping names already in use. */
export const nextSolarInverterName = (names: readonly string[]): string => {
  for (let index = 0; index < 26; index++) {
    const name = `Inverter ${String.fromCharCode(65 + index)}`;
    if (!names.includes(name)) return name;
  }
  return `Inverter ${names.length + 1}`;
};

/**
 * Longest string the cold-weather voltage allows, as the designer's
 * Auto-string: floor(Vmax / highest panel cold Voc) across the units, capped
 * at 13. A unit with no catalogue limit counts as 600 V. Undefined when no
 * panel Voc is known.
 */
export const solarVoltageMaxPanelsPerString = (args: {
  panelVocsV: ReadonlyArray<number | null | undefined>;
  /** Voc coefficient (%/°C) per entry of panelVocsV, when known. */
  panelVocTemperatureCoefficientsPctPerC?: ReadonlyArray<
    number | null | undefined
  >;
  inverterMaxInputVoltagesV: ReadonlyArray<number | null | undefined>;
  /** Site design low from siteDesignMinTemperatureC. */
  siteMinTemperatureC?: number | null;
  /** Picks the cold-voltage fallback when a coefficient is unknown. */
  countryCode?: string | null;
}): number | undefined => {
  const colds = args.panelVocsV.flatMap((value, index) =>
    value != null && value > 0
      ? [
          value *
            solarColdVocFactor({
              siteMinTemperatureC: args.siteMinTemperatureC,
              panelVocTemperatureCoefficientPctPerC:
                args.panelVocTemperatureCoefficientsPctPerC?.[index],
              countryCode: args.countryCode,
            }),
        ]
      : []
  );
  if (!colds.length) return undefined;
  const voc = Math.max(...colds);
  const limits = args.inverterMaxInputVoltagesV.length
    ? args.inverterMaxInputVoltagesV
    : [null];
  const derived = limits.map((limit) =>
    Math.floor(
      (limit != null && limit > 0 ? limit : SOLAR_ASSUMED_MAX_INPUT_VOLTAGE_V) /
        voc
    )
  );
  return Math.max(1, Math.min(SOLAR_MAX_PANELS_PER_STRING, ...derived));
};

/**
 * Roofs in the order the designer lists them when a design opens: most
 * included panels first, then most generation, then roof ID. Auto-string
 * labels roofs and breaks ties in this order.
 */
export const solarAllocationRoofOrder = (layout: {
  workingFaces: ReadonlyArray<{ id: number; azimuthDegrees: number }>;
  workingPanels: ReadonlyArray<{
    faceId: number;
    included: boolean;
    annualAcKwh: number;
  }>;
}): { id: number; azimuthDegrees: number }[] =>
  layout.workingFaces
    .map((face) => {
      const included = layout.workingPanels.filter(
        (panel) => panel.faceId === face.id && panel.included
      );
      return {
        face,
        count: included.length,
        kwh: included.reduce((sum, panel) => sum + panel.annualAcKwh, 0),
      };
    })
    .sort((a, b) => b.count - a.count || b.kwh - a.kwh || a.face.id - b.face.id)
    .map(({ face }) => ({ id: face.id, azimuthDegrees: face.azimuthDegrees }));
